import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import sounds from 'src/lib/sounds';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Avatar } from '../components/shared/Avatar';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import StateObserver from 'src/StateObserver';
import { Profile } from 'src/replicant/profile';
import platform from '@play-co/gcinstant';
import { getRandomItemFromArray } from 'src/replicant/utils/random';
import { trackProfileUpdate } from 'src/lib/analytics/events';
import { animDuration, waitForItPromise } from 'src/lib/utils';
import { TextInput } from '../components/shared/TextInput';
import { profileAvatarAssets } from 'src/replicant/creatives/assets';
import playerNames from '../../replicant/airtable/PlayerName';
import ab from 'src/replicant/ruleset/ab';
import { getDynamicTestBucket } from 'src/replicant/getters/ab';

const ui = uiConfig.buruburu.profile;

export default class PopupEditProfile extends PopupBasic<
  (canceled: boolean) => void
> {
  private inputField: TextInput;
  private avatar: Avatar;

  private get currentAvatar() {
    return this._currentAvatar;
  }
  private set currentAvatar(avatar: string) {
    this._currentAvatar = avatar;
    this.avatar.updateImage(avatar);
  }
  private _currentAvatar: string;

  private buttonOkay: ButtonScaleViewWithText;
  private buttonCancel: ButtonScaleViewWithText;

  constructor(opts: { superview: View; close: (canceled: boolean) => void }) {
    super({
      superview: opts.superview,
      close: () => opts.close(true),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 660,
      darkerBg: false,
      tracking: { feature: 'profile', $subFeature: 'popup-editProfile' },
    });

    this.buttonClose.removeFromSuperview();

    // Top Separator
    new View({
      superview: this.box,
      height: 5,
      width: this.box.style.width / 2,
      x: this.box.style.width / 2,
      y: 90,
      backgroundColor: '#e7b77e',
      centerOnOrigin: true,
    });

    this.avatar = new Avatar({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 180,
      onClick: async () => {
        sounds.sfx.play('ok');

        this.setInputFieldVisible(false);

        const currentAvatar = await openPopupPromise('popupAvatarSelection', {
          currentAvatar: this.currentAvatar,
        });
        this.currentAvatar = currentAvatar;

        this.setInputFieldVisible(true);
      },
    });

    new ImageScaleView({
      superview: this.avatar,
      image: 'assets/ui/shared/buttons/btn_edit.png',
      width: 57,
      height: 57,
      x: this.avatar.style.width * 0.9,
      y: this.avatar.style.height * 0.9,
      centerOnOrigin: true,
    });

    this.inputField = new TextInput({
      ...ui.edit.input,
      superview: this.box,
      x: this.box.style.width / 2,
      y: this.avatar.style.y + this.avatar.style.height * 0.9,
      width: this.box.style.width * 0.8,
    });

    new LangBitmapFontTextView({
      ...ui.edit.noticeText,
      superview: this.box,
      align: 'center',
      localeText: () => i18n('profile.edit.maxLength'),
      x: this.box.style.width / 2,
      y: this.inputField.style.y + this.inputField.style.height * 0.9,
      width: this.box.style.width,
      height: 27,
    });

    // Bottom Separator
    new View({
      superview: this.box,
      height: 5,
      width: this.box.style.width - 10,
      x: this.box.style.width / 2,
      y: this.box.style.height - 280,
      backgroundColor: '#e7b77e',
      centerOnOrigin: true,
    });

    this.message.updateOpts({
      ...ui.edit.noticeText,
      superview: this.box,
      x: this.box.style.width / 2,
      y: this.box.style.height - 205,
      width: this.box.style.width * 0.75,
    });

    this.buttonCancel = new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      ...ui.button,
      superview: this.box,
      x: this.box.style.width / 4,
      y: this.box.style.height - 25 - 100 / 2,
      localeText: () => i18n('basic.cancel'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close(true);
      },
      tracking: { feature: 'profile', $subFeature: 'popup-editProfile-close' },
    });

    this.buttonOkay = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      ...ui.button,
      x: (this.box.style.width / 4) * 3,
      y: this.box.style.height - 25 - 100 / 2,
      localeText: () => i18n('basic.ok'),
      onClick: async () => {
        sounds.sfx.play('ok');

        let name =
          this.inputField.input.value ?? this.inputField.input.placeholder;

        // Remove all special characters from the name, keep it alphanumeric and spaces only
        name = name.replace(/[^a-zA-Z0-9 ]/g, '').trim();

        // Replace the input with the cleaned name
        this.inputField.input.value = name;

        // Quick revalidate if the name somehow ends up longer or shorter than the allowed length
        if (
          name.length < this.inputField['minLength'] ||
          name.length > this.inputField['maxLength']
        ) {
          this.inputField['validate']();
          return;
        }

        this.setInputFieldVisible(false);

        const photo = this.currentAvatar;

        const { photo: currentPhoto, name: currentName } =
          StateObserver.getState().user.publicProfile ?? {};

        await StateObserver.invoke.updatePublicProfile({
          name,
          photo,
        });

        trackProfileUpdate({
          imageId: photo,
          isLineName: name === platform.playerName,
          imageUpdated: photo !== currentPhoto,
          nameUpdated: name !== currentName,
        });

        return opts.close(false);
      },
      tracking: { feature: 'profile', $subFeature: 'popup-editProfile-ok' },
    });
  }

  public init({ hideCloseButton = false }: { hideCloseButton?: boolean }) {
    super.init({
      title: i18n('profile.edit.title'),
      message: i18n('profile.edit.notice'),
    });

    const { name, photo } = StateObserver.getState().user.publicProfile ?? {};

    const inRandomNamesEnabled = false;

    if (inRandomNamesEnabled) {
      this.inputField.input.value = name ?? this.generateRandomName();
    } else {
      this.inputField.input.value = name ?? '';
    }

    this.currentAvatar =
      photo ??
      getRandomItemFromArray(
        Object.keys(profileAvatarAssets).map((id) =>
          StateObserver.replicant.getChatbotAssetUrl(id),
        ),
        Math.random,
      );

    const [minLength, maxLength] = Profile.ruleset.nameLength;

    this.inputField.on('InputValidated', (valid) =>
      this.buttonOkay.setDisabled(!valid),
    );

    this.inputField.updateOpts({
      minLength,
      maxLength,
      placeholder: i18n('profile.edit.placeHolder'),
    });

    if (hideCloseButton) {
      this.buttonCancel.removeFromSuperview();
      this.buttonOkay.updateOpts({
        x: this.box.style.width / 2,
      });
    }

    // TODO: Find a better way to do this.
    const linkRect = {
      en: {
        x: 315,
        y: 105,
        width: 115,
        height: 15,
      },
      ja: {
        x: 212,
        y: 110,
        width: 110,
        height: 15,
      },
    };

    addLink({
      text: this.message,
      ...linkRect[StateObserver.getState().user.settings.locale],
      onClick: async () => {
        this.setInputFieldVisible(false);
        await openPopupPromise('popupTutorialStep', {
          step: 'profile',
          page: 1,
        });
        this.setInputFieldVisible(true);
      },
    });
  }

  private setInputFieldVisible(visible: boolean) {
    // Wait animationDuration before showing/hiding the input field.
    void waitForItPromise(animDuration).then(() => {
      this.inputField.style.visible = visible;
    });
  }

  /**
   * Return random name based on PlayerName air table
   */
  private generateRandomName(): string {
    let names = [...playerNames];
    const lang = StateObserver.getLocale();

    const result = names.reduce(
      (result, current) => {
        const key = lang === 'en' ? 'name_en' : 'name_jp';
        const name = current[key];

        if (current.part === 'first') {
          result.first.push(name);
        } else {
          result.last.push(name);
        }

        return result;
      },
      {
        first: [],
        last: [],
      },
    );

    const first = getRandomItemFromArray(result.first, Math.random);
    const last = getRandomItemFromArray(result.last, Math.random);

    return `${first}${last}`;
  }
}

function addLink({
  text,
  x,
  y,
  width,
  height,
  onClick,
}: {
  text: LangBitmapFontTextView;
  x: number;
  y: number;
  width: number;
  height: number;
  onClick: () => void;
}) {
  const clickable = new View({
    superview: text,
    x,
    y,
    canHandleEvents: true,
    width,
    height,
  });

  new View({
    superview: text,
    x: x,
    y: y + height,
    backgroundColor: text.color,
    canHandleEvents: true,
    width: width,
    height: 2,
  });

  clickable.onInputSelect = onClick;
}
